<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-2023 (original work) Open Assessment Technologies SA ;
    /* eslint-disable implicit-arrow-linebreak */

    import { createEventDispatcher, tick } from 'svelte';
    import { __, getLanguageDirection, convertSizeToReadableFormat } from '@oat-sa-private/ui-core';
    import { Button, Icon } from '@oat-sa-private/ui-elements';
    import {
        convertMimeTypesToDisplayFormat,
        convertMimeTypesToAcceptAttr,
        isValidType,
        hasValidExtension
    } from './util/mimetype.js';
    import { getFirstFile, containsFolders } from './util/dataTransfer.js';

    /**
     * The FileSelect component lets the user select a file from his computer
     * @property {string} name - the HTML input's name
     * @property {string} [validMimeTypes] - space-separated list of valid mime types, each formatted as https://tools.ietf.org/html/rfc2045#section-5.1
     * @property {number} [maxSize] - to limit the size of the file
     * @property {boolean} [disabled] - the component can be disabled
     * @property {File} [value] - the selected file (will be extended with '{string} ext' property)
     * @property {string} [link] - a link to download the file
     * @property {string} [specification] - the size calculation specification
     * @property {boolean} [allowEmptyFile] - if the component allows to select empty files
     * @property {boolean} [valid] - the component valid state
     * @property {string} [customValidity] - a string that allow to give a custom validation message, the valid becomes false if set
     * @property {string} [lang] - language code for feedbacks
     * @fires change
     */

    export let name;
    export let validMimeTypes = '';
    export let hasDropArea = true;
    export let maxSize = 0;
    export let disabled = false;
    export let value = null;
    export let link;
    export let specification = 'si';
    export let allowEmptyFile = false;
    export let valid = true;
    export let customValidity = '';
    export let lang;
    let fileCount = 0;
    const maxFiles = 1;

    const dispatch = createEventDispatcher();

    /**
     * Accepted file formats, for the <input> attribute. Comma-separated.
     * @type {string}
     */
    const validTypes = convertMimeTypesToAcceptAttr(validMimeTypes);

    /**
     * User friendly format of valid mime types. Comma-separated.
     * @type {string}
     */
    const validFormatsLabel = convertMimeTypesToDisplayFormat(validMimeTypes);

    /**
     * User friendly format of file size
     * @type {string}
     */
    const validMaxSize = convertSizeToReadableFormat(maxSize, specification);

    /**
     * Reference of file input element
     * @type {HTMLInputElement}
     */
    let fileInputElement;

    /**
     * Is something dragged over the drop area
     * @type {boolean}
     */
    let dragOver = false;

    /**
     * Does user want to select another file
     * @type {boolean}
     */
    let selectAgain = false;

    /**
     * Reference of feedback element
     * @type {HTMLElement}
     */
    let feedbackElement;

    /**
     * Link for downloading
     * @type {string}
     */
    let downloadLink;

    /**
     * The message displayed when the selection is not valid
     * @type {string}
     */
    let validationMessage;

    //allow to set customValidity at any time
    $: if (customValidity && value) {
        validate(value);
    }

    $: downloadLink = link ? link : getFileLocalUrl(value);
    $: dir = lang ? getLanguageDirection(lang) : void 0;

    function getFileExtension(file) {
        if (file.name && file.name.includes('.')) {
            const ext = file.name.match(/\.([\w-]+)$/)[1];
            return ext.toLowerCase();
        }
        return null;
    }

    /**
     * Gets the reason key of invalidity
     * @param {Object} val
     * @returns {?String}
     */
    function getInvalidValidityKey(val) {
        const validators = {
            fileCount: fileCount > maxFiles,
            folder: val && val.isFolder,
            empty: !allowEmptyFile && val && (!val.size || val.size === 0),
            rangeOverflow: maxSize && val && val.size > maxSize,
            typeMismatch:
                validTypes && val && !isValidType(val.type, validTypes) && !hasValidExtension(val.ext, validTypes)
        };
        return Object.keys(validators).find(validityKey => validators[validityKey]);
    }

    /**
     * Map validity key to user friendly error message
     * @type {object<string, () => string>}
     */
    const validationMessages = {
        folder: () =>
            __(
                'Folders are not accepted, expected formats are: %s',
                validFormatsLabel ? validFormatsLabel : __('files')
            ),
        empty: () => __('Empty files are not accepted, please try again.'),
        rangeOverflow: () =>
            __(
                'The size of the selected file is %s that is more than the accepted %s',
                convertSizeToReadableFormat(value.size, specification),
                validMaxSize
            ),
        typeMismatch: () => {
            if (value.name.includes('.')) {
                return __(
                    'The %s file format is not accepted, expected formats are: %s',
                    value.name.replace(/^.*(\..*?)$/, '$1'),
                    validFormatsLabel
                );
            }
            return __('The unknown file format is not accepted, expected formats are: %s', validFormatsLabel);
        },
        fileCount: () => __('Multiple files are not accepted. Please try again.')
    };

    /**
     * Get the valid from a given value
     * @param {File?} valueToCheck - the value to validate
     */
    function validate(valueToCheck) {
        valid = !getInvalidValidityKey(valueToCheck);
        if (!valid) {
            let invalidValidityKey = getInvalidValidityKey(valueToCheck);
            validationMessage = validationMessages[invalidValidityKey]();
        } else if (customValidity) {
            valid = false;
            validationMessage = customValidity;
        } else {
            validationMessage = '';
        }
    }

    /**
     * Generate file url based on selected File
     * @param {File} file
     * @returns {string|null} return url if file selected
     */
    function getFileLocalUrl(file) {
        return file ? URL.createObjectURL(file) : null;
    }

    /**
     * Handles file input value change
     * @param {Event} e
     */
    function handleChange(e) {
        if (e.target.files[0]) {
            value = e.target.files[0];
            handleNewFileSelect();
        }
    }

    /**
     * Handle click event of 'Select' file button
     */
    function handleSelectFileButtonClick() {
        fileInputElement.click();
    }

    /**
     * Handle click event of 'Select different file' button
     */
    function handleSelectAnotherFileButtonClick() {
        selectAgain = true;

        if (customValidity) {
            //if the field was invalid due to customValidity,
            // we reset everything otherwise input in chrome won't send change events anymore
            customValidity = '';
            fileInputElement.value = '';
            value = null;
            valid = true;
        }
    }

    /**
     * Handle click event of 'Clear' button
     */
    function handleClearFileButtonClick() {
        fileInputElement.value = null;
        value = null;
        customValidity = '';
        valid = true;
        dispatch('change', { value, validity: valid });
    }

    /**
     * Handles drop to drop area
     * @param {DragEvent} e
     */
    function handleDrop(e) {
        if (disabled) {
            return;
        }
        handleDragLeave();
        if (
            e.dataTransfer &&
            e.dataTransfer.files &&
            e.dataTransfer.files.length &&
            e.dataTransfer.files.length > maxFiles
        ) {
            fileCount = e.dataTransfer.files.length;
        }

        const file = getFirstFile(e.dataTransfer);
        if (file) {
            value = file;
            if (containsFolders(e.dataTransfer)) {
                value.isFolder = true;
            }
            handleNewFileSelect();
        }
    }

    /**
     * Handles value change
     */
    function handleNewFileSelect() {
        value.ext = getFileExtension(value);

        validate(value);
        selectAgain = !valid;
        fileCount = 0;
        if (fileInputElement) {
            fileInputElement.setCustomValidity(validationMessage);
        }

        dispatch('change', { value, validity: valid });

        tick().then(() => {
            if (feedbackElement) {
                feedbackElement.focus();
            }
        });
    }

    /**
     * Handles drag over above drop area
     */
    function handleDragOver() {
        if (!disabled) {
            dragOver = true;
        }
    }

    /**
     * Handles drag leave of drop area
     */
    function handleDragLeave() {
        dragOver = false;
    }
</script>

<style>
    input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
    }
    .selected-file-container {
        text-align: center;

        &.select-again {
            margin-bottom: 6rem;
        }

        & p {
            background-color: var(--color-bg-selected);
            color: var(--color-text-selected);
            padding: var(--space-1x5) var(--space-2x);
            text-align: start;

            & :global(svg) {
                margin-inline-end: var(--space-1x);
            }

            & a {
                color: var(--color-text-link-secondary);
                &:hover {
                    color: var(--color-text-link-secondary-hover);
                }
            }
        }

        &.invalid p {
            background-color: var(--color-bg-warning);
        }
    }
    .browse-container {
        margin-top: var(--space-1x);
    }

    .label {
        font-weight: bold;
    }

    .expected-formats {
        font-size: var(--fontsize-body-xs);
        color: var(--color-text-feedback);
        font-weight: 200;
        margin: 0 0 1rem 0;
    }

    .drop-container {
        margin-top: 4rem;
    }

    .drop-area {
        position: relative;
        height: 24rem;
        background-color: var(--color-bg-selection);
        color: var(--color-text-link);
        border: var(--border-thin) solid var(--color-border-active);
        border-radius: var(--radius-large);
        margin: var(--space-1x) 0 var(--space-half);

        & span {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }

        &.drag-over {
            border: var(--border-medium-plus) dashed var(--color-border-selected);
        }
    }

    .disabled .drop-area {
        @add-mixin actionable-disabled;
    }
</style>

<div class:disabled {lang} {dir}>
    <input
        bind:this={fileInputElement}
        type="file"
        {name}
        {disabled}
        accept={validTypes ? validTypes : void 0}
        on:change={handleChange}
        aria-hidden="true"
        tabindex="-1" />
    {#if value}
        <div class="selected-file-container" class:select-again={selectAgain} class:invalid={!valid}>
            <p bind:this={feedbackElement} tabIndex="-1">
                {#if valid}
                    <span aria-label={__('File selected successfully')}>
                        <Icon name="check-16" ariaHidden={true} />
                    </span>
                    {#if downloadLink}
                        <span>{__('Answer file selected:')}</span>
                        <a href={downloadLink} download={value.name} target="_blank" rel="noreferrer">{value.name}</a>
                    {:else}{value.name}{/if}
                {:else if validationMessage}
                    {validationMessage}
                {:else if customValidity}{customValidity}{:else}{__('This file is not accepted')}{/if}
            </p>
            {#if !selectAgain}
                <Button
                    label={__('Select different file')}
                    shape="pill"
                    skin="secondary"
                    on:click={handleSelectAnotherFileButtonClick}
                    {disabled} />
            {/if}
            {#if valid}
                <Button
                    label={__('Clear file')}
                    shape="pill"
                    skin="secondary"
                    on:click={handleClearFileButtonClick}
                    {disabled} />
            {/if}
        </div>
    {/if}
    {#if !value || selectAgain}
        <span class="label">{__('Browse your computer')}</span>
        <div class="browse-container">
            <Button label={__('Select a file')} shape="pill" on:click={handleSelectFileButtonClick} {disabled} />
        </div>
        {#if validFormatsLabel && validFormatsLabel.length}
            <p class="expected-formats">{__('expected formats: %s', validFormatsLabel)}</p>
        {/if}
        {#if maxSize}
            <p class="expected-formats">{__('maximum file size: %s', validMaxSize)}</p>
        {/if}
        {#if hasDropArea}
            <div class="drop-container" aria-hidden="true">
                <div
                    class="drop-area"
                    class:drag-over={dragOver}
                    on:dragover|preventDefault={handleDragOver}
                    on:dragenter={handleDragOver}
                    on:dragleave={handleDragLeave}
                    on:dragend={handleDragLeave}
                    on:drop|preventDefault={handleDrop}>
                    <span>{__('..or drop your file here')}</span>
                </div>
            </div>
        {/if}
    {/if}
</div>
